import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import MicrosoftComplete from "@/pages/MicrosoftComplete";
import Dashboard from "@/pages/Dashboard";
import Opportunities from "@/pages/Opportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import Investors from "@/pages/Investors";
import Matching from "@/pages/Matching";
import Pipeline from "@/pages/Pipeline";
import Commissions from "@/pages/Commissions";
import Ndas from "@/pages/Ndas";
import IssuerSettings from "@/pages/IssuerSettings";
import Radar from "@/pages/Radar";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Subscription from "@/pages/Subscription";
import Account from "@/pages/Account";
import AdminClients from "@/pages/AdminClients";
import BillingSettings from "@/pages/BillingSettings";
import Landing from "@/pages/Landing";
import Prospects from "@/pages/Prospects";
import AdminSecurity from "@/pages/AdminSecurity";
import AdminCosts from "@/pages/AdminCosts";
import ConditionsGenerales from "@/pages/ConditionsGenerales";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null)
    return (
      <div className="flex h-screen items-center justify-center text-slate-400" data-testid="app-loading">
        Chargement…
      </div>
    );
  // Préserve l'URL demandée (ex. lien e-mail /radar?listing=<id>) pour y revenir après connexion.
  if (!user) return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  return children;
}

function StaffOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "superadmin") return <Navigate to="/radar" replace />;
  return children;
}

function RadarGuard({ children }) {
  const { user } = useAuth();
  const sub = user?.subscription || {};
  // Un essai express expiré (ou toute personne ayant déjà démarré un essai) garde l'accès
  // à /radar en LECTURE SEULE (résultats verrouillés). Seuls les comptes sans aucun essai
  // (status "none") sont redirigés vers l'abonnement.
  const hadTrial = !!(sub.trial_kind || sub.trial_end || sub.trial_started_at || sub.status === "expired");  if (user?.role !== "superadmin" && !user?.radar_access && !hadTrial) {
    return <Navigate to="/abonnement" replace />;
  }
  return children;
}

function Home() {
  return <Navigate to="/radar" replace />;
}

function PublicRoot() {
  const { user, loading } = useAuth();
  if (loading)
    return <div className="flex h-screen items-center justify-center text-slate-400" data-testid="public-loading">Chargement…</div>;
  if (user) return <Navigate to="/tableau-de-bord" replace />;
  return <Landing />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoot />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/conditions-generales" element={<ConditionsGenerales />} />
      <Route path="/auth/microsoft/complete" element={<MicrosoftComplete />} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/tableau-de-bord" element={<RadarGuard><Dashboard /></RadarGuard>} />
        <Route path="/opportunites" element={<StaffOnly><Opportunities /></StaffOnly>} />
        <Route path="/opportunites/:id" element={<StaffOnly><OpportunityDetail /></StaffOnly>} />
        <Route path="/investisseurs" element={<StaffOnly><Investors /></StaffOnly>} />
        <Route path="/mise-en-relation" element={<StaffOnly><Matching /></StaffOnly>} />
        <Route path="/suivi" element={<StaffOnly><Pipeline /></StaffOnly>} />
        <Route path="/commissions" element={<StaffOnly><Commissions /></StaffOnly>} />
        <Route path="/nda" element={<StaffOnly><Ndas /></StaffOnly>} />
        <Route path="/radar" element={<RadarGuard><Radar /></RadarGuard>} />
        <Route path="/abonnement" element={<Subscription />} />
        <Route path="/mon-compte" element={<Account />} />
        <Route path="/admin" element={<StaffOnly><AdminClients /></StaffOnly>} />
        <Route path="/admin/securite" element={<StaffOnly><AdminSecurity /></StaffOnly>} />
        <Route path="/admin/couts" element={<StaffOnly><AdminCosts /></StaffOnly>} />
        <Route path="/prospects" element={<StaffOnly><Prospects /></StaffOnly>} />
        <Route path="/parametres-facturation" element={<StaffOnly><BillingSettings /></StaffOnly>} />
        <Route path="/parametres-emetteur" element={<StaffOnly><IssuerSettings /></StaffOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
